import {
  Box,
  Grid,
  Typography,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  Stack,
  InputBase,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Tooltip,
  Snackbar, 
  Alert,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Search,
  Add,
  FileUploadOutlined,
  FileDownloadOutlined,
  Edit,
  Delete,
  ArrowCircleDownOutlined,
  LeaderboardOutlined,
  PersonOutlined,
  StarOutlineOutlined,
} from '@mui/icons-material';
import { useCallback, useEffect, useMemo, useState } from 'react';

import * as XLSX from 'xlsx';
import { useGridApiRef, gridFilteredSortedRowIdsSelector, gridVisibleColumnFieldsSelector, DataGrid } from '@mui/x-data-grid';

import EditLeadModal from './Leads/EditLeadModal';
import ConfirmDialog from './Leads/ConfirmDialog';
import AddLeadModal from './Leads/AddLeadModal';

import { addLead, deleteLead, getLeads, getLeadsAnalytics, updateLead } from '../api/apiService';
import LeadUploadModal from './Leads/LeadUploadModal';

export default function LeadsPage()  {
  const clientId = JSON.parse(localStorage.getItem('clientId')); // Client Id for fetching details like leads.. 
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const apiRef = useGridApiRef();

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(1); // 1-based index
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);

  const totalPages = Math.ceil(rowCount / pageSize);
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, rowCount);

  const [editOpen, setEditOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deletedName, setDeletedName] = useState(null);

  const [searchText, setSearchText] = useState('');
  const [displayedRows, setDisplayedRows] = useState([]); 

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [leadScoreLoading, setLeadScoreLoading] = useState(false);

  const [uploadOpen, setUploadOpen] = useState(false);

  const [analytics, setAnalytics] = useState(null);
  //const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  /* Columns for data grid */
  const columns = useMemo( () => [
    { field: 'fullName', headerName: 'Full Name', width: 160,
        description: 'Full Name',
        valueGetter: (value, row) => `${row.firstName || ''} ${row.lastName || ''}`,
         renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params?.value || " - " }
        </Box>
      )
    },
    { field: 'email', headerName: 'Email' , width:220,
    renderCell: (params) => (
    <Box 
    sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        whiteSpace: 'normal',   // allow wrapping
        wordBreak: 'break-word', // wrap long words like emails
        overflowWrap: 'break-word',
    }}
    >
        {params?.value || " - " }
    </Box>
    )
    },
    { field: 'company', headerName: 'Company', width:200, 
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params?.value || " - " }
        </Box>
      )
    },
    { field: 'jobTitle', headerName: 'Job Title', width:100,
    renderCell: (params) => (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        {params?.value || " - " }
    </Box>
      )
    },
    { field: 'industry', headerName: 'Industry', width:100,
    renderCell: (params) => (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        {params?.value || " - " }
    </Box>
      )
    },
    { field: 'companySize', headerName: 'Company Size', width:100,
    renderCell: (params) => (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        {params?.value || " - " }
    </Box>
      )
    },
    { field: 'website', headerName: 'Website', width:200,
     renderCell: (params) => (
        <a
        href={params.value}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#1976d2', textDecoration: 'underline' }}
        >
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        {params.value}
    </Box>
        </a>
    ),
    },
    { field: 'score', headerName: 'AI Score', width:100,
        type:'number',
        renderCell:(params) => (
        <Box sx={{my:2.2}}>
        <Stack gap={1} sx={{flexDirection:"row", alignItems:"center"}}>
            <Stack sx={{ flexGrow: 1}}>
                {
                params.value===0? 'N/A'
                :
                <LinearProgress sx={{py:0.5, borderRadius:1}} variant="determinate" color={params.value > 75 ? 'green' : params.value < 65 ? 'red' : 'yellow'} value={params.value} />
                }
            </Stack>
            <Typography variant="body2" fontWeight={500}>{params.value===0?'':params.value}</Typography>
        </Stack>
        </Box>
        )
    },
    { field: 'tags', headerName: 'Tags', minWidth: 200,
      flex: 1,
      sortable: false,
      renderCell: (params) => (
       <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap', // Enables wrapping
            gap: 1,
            alignItems: 'center',
            width: '100%', // Important for full cell width
            overflowWrap: 'anywhere', // Helps long tags wrap
            my:2
          }}
        >
          {params.value?.length > 0 ? (
            params.value.map((tag, idx) => (
              <Chip
                key={idx}
                label={tag}
                color="primary"
                variant="outlined"
                size="small"
              />
            ))
          ) : (
            <Typography variant="caption" color="textDisabled">No Tags</Typography>
          )}
        </Box>
      ),
    },
    { field: 'linkedin', headerName: 'LinkedIn', width:230,
     renderCell: (params) => (
        <a
        href={params.value}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#1976d2', textDecoration: 'underline' }}
        >
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        {params.value}
        </Box>
        </a>
    ),
    },
    { field: 'phone', headerName: 'phone', width:140,
    renderCell: (params) => (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        {params?.value || " - " }
    </Box>
      )
    },
    { field: 'country', headerName: 'Country', width:100,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params?.value || " - " }
        </Box>
      )
    },
    { field: 'state', headerName: 'State', width:100,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params?.value || " - " }
        </Box>
      )
    },
    { field: 'city', headerName: 'City', width:100,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params?.value || " - " }
        </Box>
      )
    },
    { field: 'createdAt', headerName: 'Created', width:100,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value}
        </Box>
      )
    },
    { field: 'actions', headerName: 'Actions', width:100 , 
      sortable:false, 
      filterable:false,
      disableColumnMenu:true, //hides 3-dot
      disableExport:true,
      headerClassName:'non-selectable-column',
      type:'actions',
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Edit">
            <IconButton 
              color="primary" 
              size="small" 
              onClick={() => handleEditClick(params.row)}
            >
              <Edit fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton 
              color="red" 
              size="small" 
              onClick={() => handleDeleteClick(params.row.id, params.row.fullName)}
            >
              <Delete fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ], []);

  const fetchLeads = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
     await getLeads(clientId, page, pageSize)
     .then((res) => {
         const normalizedRows = res.data.leads.map((lead) => ({
         ...lead,
        createdAt: new Date(lead.createdAt?.$date || lead.createdAt).toLocaleDateString(),
        updatedAt: new Date(lead.updatedAt?.$date || lead.updatedAt).toLocaleDateString(),
         }));
         setRows(normalizedRows);
         setRowCount(res.data.total);
         // console.log('Normalized leads:', normalizedRows);
 
     })
     .catch((err) => {
     setLoading(false);
     console.log('Failed to load leads:', err);
     //alert('Failed to load leads');
     setSnackbar({ open: true, message: 'Failed to load leads', severity: 'error' });
     })
     .finally(() => setLoading(false));
  },[clientId, page, pageSize]);

  const fetchLeadsAnalytics = useCallback(async () => {
    if (!clientId) return;
    //setLoadingAnalytics(true);

    await getLeadsAnalytics(clientId)
      .then((res) => {
        if (res.success) {
          setAnalytics(res.data);
        } else {
          setSnackbar({
            open: true,
            message: res.message || "Failed to load analytics",
            severity: "error",
          });
        }
      })
      .catch((err) => {
        console.error("Failed to load analytics:", err);
        setSnackbar({
          open: true,
          message: "Failed to load analytics",
          severity: "error",
        });
      })
      //.finally(() => setLoadingAnalytics(false));
  }, [clientId]);

  /* Load leads data on first load */
   useEffect(() => {
     fetchLeads();
     fetchLeadsAnalytics();
   }, [fetchLeads, fetchLeadsAnalytics]);

  /* Search - Load filtered data to data grid from search bar*/ 
    useEffect(() => {
    const filtered = rows.filter((row) => {
      const search = searchText.toLowerCase();
      return (
        row.name?.toLowerCase().includes(search) ||
        row.email?.toLowerCase().includes(search) ||
        row.company?.toLowerCase().includes(search) ||
        row.jobTitle?.toLowerCase().includes(search) ||
        row.industry?.toLowerCase().includes(search) ||
        row.companySize?.toLowerCase().includes(search) ||
        row.country?.toLowerCase().includes(search) ||
        row.state?.toLowerCase().includes(search) ||
        row.city?.toLowerCase().includes(search) ||
        row.score?.toString().includes(search) ||
        row.tags?.some(tag => tag.toLowerCase().includes(search))
      );
    });

    setDisplayedRows(filtered);
  }, [rows, searchText]);

  /* Add button click - for add lead Modal open */
  const handleAdd = () => {
    setSelectedLead(null);
    setAddOpen(true);
  }
  /*Add new lead - for adding lead to database via api */
  const handleAddSave = (lead) => {

    const payload = {
      firstName:lead.firstName,
      lastName:lead.lastName,
      email:lead.email,
      company:lead.company,
      jobTitle:lead.jobTitle,
      industry:lead.industry,
      companySize:lead.companySize,
      website:lead.website,
      phone:lead.phone,
      linkedin:lead.linkedin,
      country:lead.country,
      state:lead.state,
      city:lead.city,
      status: lead.status || "new",  // default value
      tags: lead.tags || [],
      source: lead.source || "", // default value
      notes: lead.notes || "", // default value
      clientId: clientId,  
    };
      setLeadScoreLoading(true);
      addLead(payload) // Api call for add leaad
        .then((res) => {
          setLeadScoreLoading(false);
          //setRows(prev => [...prev, res.data]); // Update DataGrid (mutating sometimes cause sync issues)
          setAddOpen(false); //Close modal 
          fetchLeads(); // To refresh lead data
          fetchLeadsAnalytics(); // To refresh lead analytics
          setSnackbar({ open: true, message: `${lead.firstName} added successfully`, severity: 'success' }); // show feedback
        })
        .catch(err => {
          setLeadScoreLoading(false);
          console.log("Add lead failed", err);
          try{
            setSnackbar({ open: true, message: err.message?.message || 'Failed to Add', severity: 'error' });
          }catch(err){
             setSnackbar({ open: true, message: 'Failed to Add lead', severity: 'error' });
          }
        });

  }
  /*Export leads data as xlsx - for exporting leads data from data grid to xlsx file (Excel) */
  const handleExport = () => {
    if (rows.length === 0){
      return  setSnackbar({ open: true, message: `No leads data to export`, severity: 'warning' });
    }

    const exportData = rows.map(row => ({
    //Id: row.id,
    FirstName: row.firstName,
    LastName: row.lastName,
    Email: row.email,
    Company: row.company,
    JobTitle: row.jobTitle,
    Industry: row.industry,
    CompanySize: row.companySize,
    Website: row.website,
    Phone: row.phone,
    LinkedIn: row.linkedin,
    Country: row.country,
    State: row.state,
    City: row.city,
    Status: row.status,
    Score: row.score,
    Tags: (row.tags || []).join(', '),
  }));
   
  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Leads');

  XLSX.writeFile(workbook, 'leads_export.xlsx');
  }
  /*Export filtered leads data as xlsx - for exporting filtered data from data grid as xlsx (Excel)*/
  const handleFilteredExport = () => {
    if (!apiRef.current) return;

    const visibleColumns = gridVisibleColumnFieldsSelector(apiRef).filter(field => field !== 'id' && field!== 'actions');
    const filteredRowIds = gridFilteredSortedRowIdsSelector(apiRef);

    if (filteredRowIds.length === 0) {
      return setSnackbar({ open: true, message: `No leads data to export`, severity: 'warning' });
    }

    const dataToExport = filteredRowIds.map((id) => {
      const row = apiRef.current.getRow(id);
      const filteredRow = {};
      visibleColumns.forEach((field) => {
        //filteredRow[field] = row[field];
        filteredRow[field] = Array.isArray(row[field]) ? row[field].join(', ') : row[field];
      });
      return filteredRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");
    XLSX.writeFile(workbook, "filtered_leads.xlsx");
  }
 /*Edit button click - for edit lead Modal open */
  const handleEditClick = (lead) => {
    setSelectedLead(lead);
    setEditOpen(true);
  };
 /*Edit lead - for updating lead to database via api */
  const handleEditSave = (lead) => {
     const payload = {
      firstName:lead.firstName,
      lastName:lead.lastName,
      email:lead.email,
      company:lead.company,
      jobTitle:lead.jobTitle,
      industry:lead.industry,
      companySize:lead.companySize,
      website:lead.website,
      phone:lead.phone,
      linkedin:lead.linkedin,
      country:lead.country,
      state:lead.state,
      city:lead.city,
      tags:lead.tags,
     }
     setLeadScoreLoading(true);
      updateLead(lead.id, payload)
        .then(() => {
          setLeadScoreLoading(false);
         // setRows(prev => prev.map(row => row.id === lead.id ? lead : row));
          setEditOpen(false);
          fetchLeads(); // To refresh lead data
          fetchLeadsAnalytics(); // To refresh lead analytics
          setSnackbar({ open: true, message: `${lead.firstName} Updated successfully`, severity: 'success' });
        })
        .catch(err => {
          setLeadScoreLoading(false);
          console.log("Update failed", err);
          console.log(lead.id,lead);
          setSnackbar({ open: true, message: `Update failed`, severity: 'error' });
        });

  };
 /*Delete button click - for delete lead Modal open */
  const handleDeleteClick = (id, name) => {
    setDeleteId(id);
    setDeletedName(name);
    setConfirmOpen(true);
  };
 /*Delete lead - for deleting lead to database via api */
  const handleDeleteConfirm = () => {
      deleteLead(deleteId)
      .then(() => {
        setSnackbar({ open: true, message: `Lead deleted successfully`, severity: 'success' });
        setRows(prev => prev.filter(row => row._id !== deleteId));
        fetchLeads(); // To refresh lead data
        fetchLeadsAnalytics(); // To refresh lead analytics
        setConfirmOpen(false);
      })
      .catch(err => {
        console.error("Delete failed", err);
        setSnackbar({ open: true, message: `Delete failed`, severity: 'error' });
      });
  };

  return (
    <Box p={isMobile ? 0 : 0} sx={{background: 'linear-gradient(180deg, #f8fafc 0%, #eef2ff 60%, #ffffff 100%)'}}>
      {/* Error display via Alert and snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      {/* Content heading */}
      <Typography variant="h5" fontWeight={600}>
        Leads Management
      </Typography>

     {/* Buttons and Search bar*/}
      <Grid container spacing={1} alignItems="center" sx={{py:2}}>
        {/* Buttons Group */}
        <Grid  size={{xs:12, md:9}}>
          <Grid container spacing={1}>
            {/* Upload Lead */}
            <Grid  size={{xs:6, sm:'auto'}}>
              <Button
                fullWidth
                size="medium"
                variant="contained"
                startIcon={<FileUploadOutlined />}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  //bgcolor: '#3730a3',
                  '&:hover': { bgcolor: '#312e81' }
                }}
                //onClick={handleUpload}
                onClick={() => setUploadOpen(true)}
              >
                Upload Leads
              </Button>
            </Grid>

            {/* Export filtered */}
            <Grid  size={{xs:6, sm:'auto'}}>
              <Button
                fullWidth
                size="medium"
                variant="outlined"
                startIcon={<FileDownloadOutlined />}
                sx={{ borderRadius: 2, textTransform: 'none' }}
                onClick={handleFilteredExport}
              >
                Export Filtered
              </Button>
            </Grid>
            {/* Export All */}
            <Grid  size={{xs:6, sm:'auto'}}>
              <Button
                fullWidth
                size="medium"
                variant="outlined"
                startIcon={<FileDownloadOutlined />}
                sx={{ borderRadius: 2, textTransform: 'none' }}
                onClick={handleExport}
              >
                Export All
              </Button>
            </Grid>
            {/* Add Lead */}
            <Grid  size={{xs:6, sm:'auto'}}>
              <Button
                fullWidth
                size="medium"
                variant="outlined"
                startIcon={<Add />}
                sx={{ borderRadius: 2, textTransform: 'none' }}
                onClick={handleAdd}
              >
                Add Lead
              </Button>
            </Grid>

          </Grid>
        </Grid>

        {/* Search bar */}
        <Grid  size={{xs:12, md:3}}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              border: '1px solid #abaae6ff',
              borderRadius: 2,
              px: 1,
              py: 0.5
            }}
          >
            <IconButton size="small">
              <Search color="primary" />
            </IconButton>
            <InputBase
              placeholder="Search leads..."
              inputProps={{ 'aria-label': 'search leads' }}
              sx={{ fontSize: 14, px: 1, flex: 1 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 50 } }}
            />
          </Box>
        </Grid>
      </Grid>

      {/* Leads Summary Cards */}
       <Grid container justifyContent='space-between' spacing={2} sx={{mb:2}}>
        {[
          { title: 'Total Leads', value: analytics?.totalLeads ?? 0, change: '', icon: <PersonOutlined fontSize="large" color="primary" /> },
          { title: 'High Score Leads', value: analytics?.engagementLevels?.high ?? 0, change: '>= 80', icon: <StarOutlineOutlined fontSize="large" color="primary" /> },
          { title: 'Low Score Leads', value: analytics?.engagementLevels?.low ?? 0, change: '< 50', icon:  <ArrowCircleDownOutlined fontSize="large" color="primary" /> },
          { title: 'Average Score', value: (analytics?.averageScore ?? 0).toFixed(1),  change: '', icon: <LeaderboardOutlined fontSize="large" color="primary" /> },
        ].map((item, i) => (
          <Grid key={i}   size={{xs:6, sm:4, md:3}} >
            <Card elevation={0} sx={{borderRadius:2, p:0, transition: 'transform 200ms ease, box-shadow 200ms ease', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }}}>
              <CardContent>
                  <Box sx={{ display: 'flex', justifyContent:'space-between'}}>
                    {item.icon}
                    <Typography variant="caption" fontSize="medium" sx={{my:0, color:"#3fc56eff"}}>
                      {item.change}
                    </Typography>
                  </Box>
                <Typography variant="subtitle2" color="textSecondary" sx={{mt:1}}>
                  {item.title}
                </Typography>
                <Typography variant="h5" sx={{fontWeight:"bold"}}>{item.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
        </Grid>
       
      {/* Data Grid */}
      <Box sx={{height:510, width:'100%'}}>
        <Card elevation={0} sx={{borderRadius:2}}>
            <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Leads Table
                </Typography>

                {/* Pagination Controls */}
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                    sx={{ my: 2 }}
                >
                    {/* 1. Page Info */}
                    <Typography variant="body2" color="text.secondary">
                    {rowCount === 0
                        ? 'No records'
                        : `Showing ${from}–${to} of ${rowCount} records`}
                    </Typography>

                    {/* 2. Pagination Buttons */}
                    <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                    shape="rounded"
                    size="small"
                    />
                </Stack>

                <DataGrid
                    sx={{height:600, borderRadius:2}}
                    rows={displayedRows} //rows
                    columns={columns}
                    getRowId={(row) => row.id}
                    loading={loading}
                    apiRef={apiRef}
                    rowCount={rowCount}
                    paginationMode="server"
                    hideFooterPagination
                    getRowHeight={() => 'auto'}
                />

                {/* Pagination Controls */}
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                    sx={{ mt: 2 }}
                >
                    {/* 1. Page Info */}
                    <Typography variant="body2" color="text.secondary">
                    {rowCount === 0
                        ? 'No records'
                        : `Showing ${from}–${to} of ${rowCount} records`}
                    </Typography>

                    {/* 2. Page Size Dropdown */}
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel id="page-size-label">Rows per page</InputLabel>
                    <Select
                        labelId="page-size-label"
                        value={pageSize}
                        label="Rows per page"
                        onChange={(e) => {
                        setPageSize(e.target.value);
                        setPage(1); // Reset to first page
                        }}
                    >
                        {[10, 20, 50].map((size) => (
                        <MenuItem key={size} value={size}>
                            {size}
                        </MenuItem>
                        ))}
                    </Select>
                    </FormControl>

                    {/* 3. Pagination Buttons */}
                    <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                    shape="rounded"
                    size="small"
                    />
                </Stack>
            </CardContent>
        </Card>
      </Box>

      {/* Modal for edit - only opens on Edit button on data grid click */}
        <EditLeadModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          leadData={selectedLead}
          onSave={handleEditSave}
          leadScoreGenLoading={leadScoreLoading}
        />
      {/*  Modal for delete confirmation - only opens on Delete button on data grid click  */}
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={handleDeleteConfirm}
          title={deletedName}
        />
        {/* Modal for add lead - only opens on Add lead button on data grid click */}
        <AddLeadModal
        open={addOpen}
        onClose={()=>setAddOpen(false)}
        leadData={selectedLead}
        onSave={handleAddSave}
        leadScoreGenLoading={leadScoreLoading}
        />
        {/* Upload leads */}
        <LeadUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        clientId={clientId}
        onUploadSuccess={fetchLeads} // Optional: to refresh table
      />

    </Box>  
  );
};


